/**
 * 应用外壳：PC 用左侧边栏，手机用底部 tab 栏（见方案 2.5 多端适配）。
 * 用一个组件覆盖两种形态，避免"移动端布局和桌面端布局各写一套"导致的体验漂移。
 */
import type { ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { backToFor } from '../../lib/backNav';
import { BuildStamp } from './BuildStamp';
import { PARENT_NAV, STUDENT_NAV, type NavItem } from './navConfig';

/**
 * 「构建于 …」版本标记现在住在 `./BuildStamp.tsx`（渲染）+
 * `../../lib/buildStamp.ts`（文案与兜底）。**从家长端搬出去的理由**是管理后台
 * 也要显示这一行：两处各写一份，格式迟早会漂移；一个实现、一个来源，
 * 管理端拿到的就是家长端那同一个 `__BUILD_TIME__`。
 *
 * 本外壳渲染它两处：PC 侧边栏页脚；手机端侧边栏整个不显示（`hidden md:flex`），
 * 所以头部再放一处（`md:hidden`），保证**每一页都能看到**。
 */

interface NavListProps {
  items: NavItem[];
  /** 在导航项上显示角标数量（如待处理共享请求）。 */
  badges?: Record<string, number>;
  variant: 'sidebar' | 'bottom';
  onNavigate?: () => void;
}

function NavList({ items, badges, variant, onNavigate }: NavListProps) {
  return (
    <>
      {items.map((item) => {
        const badge = badges?.[item.to] ?? 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'relative flex items-center gap-3 rounded-xl font-medium transition-colors',
                variant === 'bottom'
                  ? /**
                     * 手机底部 tab 的窄屏健壮性。
                     *
                     * 学生端本轮多了一项（「作业」，见 `navConfig` 的 `STUDENT_NAV`），
                     * 于是最窄的主流机型（320px）上 5 项均分只剩约 58px 一项。
                     * 处理方式是**压缩自己的留白**，而不是删掉别人的入口：
                     *  - `px-0.5`（原 `px-1`）：每项多出 4px 给文字；
                     *  - `whitespace-nowrap`：宁可让最长的「今日打卡」顶到边缘，
                     *    也不让它折成两行 —— 折行会把 tab 栏顶高、压住页面内容
                     *    （底部栏是 `fixed`，页面靠 `pb-[calc(4.5rem+…)]` 让位，
                     *    一旦它自己变高，最后一行内容就被盖住了）。
                     * 中文标签在 12px 下约 48px 宽，仍在 58px 之内，不会溢出。
                     */
                    'flex-1 flex-col gap-1 whitespace-nowrap px-0.5 py-2 text-xs'
                  : 'px-3 py-2.5 text-sm',
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              )
            }
          >
            {/*
              导航图标：两种写法（emoji 字符串 / 矢量图标组件）走**同一个** span ——
              于是"图标占多宽"这件事只在这里决定一次，不会出现"某一项自己写了字号"。
              矢量图标自带 `aria-hidden`（见 Icons.tsx 的底座），emoji 由这里标掉。
              尺寸：底部 tab 22px / 侧边栏 20px，与原来的 `text-xl` / `text-lg`
              视觉重量一致（图标认 `currentColor`，所以选中态的 brand-700 会跟着变）。

              ⚠️ **固定高度 + `leading-none`**（2026-10-30 用户报的）：
              > "工具那个模块按钮好像比其他三个偏高一点……没对齐。"

              emoji 的行盒高度由字体决定（约 1.2em），矢量图标是严格的 22px ——
              两者混排时，图标那一格高度不一致，下面的文字标签就错位了
              （「工具」是 SVG，于是它比三个 emoji 项目高一条）。
              这里把图标格**钉死高度**（底部 22px / 侧栏 20px），两条写法就都占同样高，
              四个 tab 的文字基线因此完全对齐。
            */}
            <span
              aria-hidden="true"
              className={cn(
                'flex items-center justify-center leading-none',
                variant === 'bottom' ? 'h-[22px] text-xl' : 'h-5 text-lg',
              )}
            >
              {item.iconComponent ? (
                <item.iconComponent size={variant === 'bottom' ? 22 : 20} />
              ) : (
                item.icon
              )}
            </span>
            <span>{item.label}</span>
            {badge > 0 && (
              <span
                className={cn(
                  'absolute flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-semibold leading-[18px] text-white',
                  variant === 'bottom' ? 'right-3 top-1' : 'right-2',
                )}
                aria-label={`${badge} 项待处理`}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </NavLink>
        );
      })}
    </>
  );
}

export interface AppShellProps {
  children: ReactNode;
  /** 家长端 / 学生端用不同的导航。 */
  kind: 'parent' | 'student';
  title?: string;
  /**
   * 可选：给左上角那个「返回」补一个**目标页的名字**（如「目标」），
   * 渲染成一个带字的胶囊按钮，供「打卡任务」这种从某个页面钻进来、
   * 且层级与它并列的页面使用。
   *
   * ⚠️ 它**不改变返回目标** —— 目标永远由当前路由推导（见 `lib/backNav.ts`），
   * 传进来的只是**同一个链接的文案**。传了它也只有**一个**返回入口（一个 `<Link>`）。
   */
  backLabel?: string;
  /** 学生端儿童模式：去掉多余装饰，聚焦内容。 */
  compactHeader?: boolean;
  badges?: Record<string, number>;
  headerAction?: ReactNode;
}

export function AppShell({
  children,
  kind,
  title,
  backLabel,
  compactHeader = false,
  badges,
  headerAction,
}: AppShellProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = kind === 'parent' ? PARENT_NAV : STUDENT_NAV;
  const brandHref = kind === 'parent' ? '/' : '/student';
  /**
   * 返回链接**只在这里算一次**，而且只看当前路由（见 `lib/backNav.ts`）：
   *  - 页面不再自己传 `backTo`，也就不可能再摆出第二个「返回」按钮；
   *  - 不用 `navigate(-1)`（历史后退）—— 那正是"两个页面之间来回弹"的根因：
   *    历史里刚离开的往往就是当前页的下钻子页。
   */
  const backLink = backToFor(location.pathname);

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex w-full max-w-7xl">
        {/* ---------- PC / iPad 侧边栏 ---------- */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-slate-200 bg-white px-3 py-5 md:flex">
          <NavLink to={brandHref} className="mb-6 flex items-center gap-2 px-2">
            <span aria-hidden="true" className="text-2xl">
              📚
            </span>
            <span className="text-base font-bold text-slate-900">家庭学习打卡</span>
          </NavLink>

          <nav className="flex flex-1 flex-col gap-1" aria-label="主导航">
            <NavList items={items} variant="sidebar" {...(badges ? { badges } : {})} />
          </nav>

          <div className="mt-4 border-t border-slate-200 pt-4">
            <p className="truncate px-2 text-sm font-medium text-slate-800">
              {user?.nickname ?? '未登录'}
            </p>
            <p className="truncate px-2 text-xs text-slate-500">
              {kind === 'parent' ? (user?.relationLabel ?? '家长') : '学生'}
            </p>
            <button
              type="button"
              onClick={onLogout}
              className="mt-3 w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              退出登录
            </button>
            {/* 构建版本：只是"确认当前页面是哪个构建"，不加按钮也不抢视觉重量 */}
            <BuildStamp className="mt-3 px-2" />
          </div>
        </aside>

        {/* ---------- 主内容区 ---------- */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header
            className={cn(
              'sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur',
              compactHeader ? 'py-2.5' : 'py-3',
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              {backLink &&
                (backLabel ? (
                  /*
                    带字的返回：**同一个 `<Link>`**，只是把「←」换成了「← 目标」。
                    宽度仍是内容撑开、且 `shrink-0` —— 按钮列不参与收缩，
                    标题列 `min-w-0` 自己让位（窄屏约定，别再改回去）。
                  */
                  <Link
                    to={backLink.to}
                    aria-label={backLink.label}
                    className="-ml-1 flex shrink-0 items-center gap-1 whitespace-nowrap rounded-xl px-2 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <span aria-hidden="true" className="text-lg leading-none">
                      ←
                    </span>
                    <span>{backLabel}</span>
                  </Link>
                ) : (
                  <Link
                    to={backLink.to}
                    aria-label={backLink.label}
                    className="-ml-1 shrink-0 rounded-xl p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <span aria-hidden="true" className="text-lg leading-none">
                      ←
                    </span>
                  </Link>
                ))}
              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold text-slate-900">
                  {title ?? (kind === 'parent' ? '家庭学习打卡' : '今天要做什么')}
                </h1>
                {!compactHeader && (
                  <p className="truncate text-xs text-slate-500">
                    你好，{user?.nickname ?? '同学'}
                  </p>
                )}
                {/* 手机上没有侧边栏，构建版本改在这里显示（同一组件，只有一处实现） */}
                <BuildStamp className="md:hidden" />
              </div>
            </div>
            {/* 按钮列不参与收缩（`shrink-0`）：窄屏上让标题列先让位，
                否则「退出」会被挤成两行、把头部顶高 —— 这是本文件既有的约定。 */}
            <div className="flex shrink-0 items-center gap-2">
              {headerAction}
              {/* 手机上把退出登录放在头部，侧边栏在手机上不显示。
                  ⚠️ 这是**退出登录**，与返回无关：两者永远各自独立，
                  合并成一个控件会让"想返回却登出了"变成一次误操作。 */}
              <button
                type="button"
                onClick={onLogout}
                className="whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 md:hidden"
              >
                退出
              </button>
            </div>
          </header>

          <main
            className={cn(
              'flex-1 px-4 py-4 sm:px-6 sm:py-6',
              // 给底部 tab 栏留位置（含 iPhone 安全区）
              'pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-8',
            )}
          >
            {children}
          </main>
        </div>
      </div>

      {/* ---------- 手机底部 tab 栏 ---------- */}
      <nav
        aria-label="主导航"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-slate-200 bg-white px-2 pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <NavList items={items} variant="bottom" {...(badges ? { badges } : {})} />
      </nav>
    </div>
  );
}
